import axios, { type AxiosError, type AxiosInstance } from "axios";
import type {
  Apartment,
  ApartmentDetail,
  ApartmentSearchParams,
  ApartmentWithMatchCount,
  DashboardStats,
  Filter,
  FilterBulkChanges,
  FilterOptions,
  MapApartment,
  NotificationStats,
  PaginatedResponse,
  PriceChange,
  RejectedApartment,
  RejectionStats,
  ScrapeExecution,
  ScrapeExecutionDetail,
  Settings,
  User,
} from "./types";

const api: AxiosInstance = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Redirect to login on unauthorized
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: async (username: string, password: string): Promise<User> => {
    const { data } = await api.post<User>("/auth/login", { username, password });
    return data;
  },

  logout: async (): Promise<void> => {
    await api.post("/auth/logout");
  },

  me: async (): Promise<User> => {
    const { data } = await api.get<User>("/auth/me");
    return data;
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await api.post("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },
};

// Apartments API
export const apartmentsApi = {
  list: async (
    params: ApartmentSearchParams & {
      ids?: number[];
      page?: number;
      page_size?: number;
    }
  ): Promise<PaginatedResponse<Apartment>> => {
    const { ids, ...rest } = params;
    const { data } = await api.get<PaginatedResponse<Apartment>>("/apartments", {
      params: {
        ...rest,
        ids: ids?.join(","),
      },
    });
    return data;
  },

  getFilterOptions: async (): Promise<FilterOptions> => {
    const { data } = await api.get<FilterOptions>("/apartments/filter-options");
    return data;
  },

  get: async (id: number): Promise<ApartmentDetail> => {
    const { data } = await api.get<ApartmentDetail>(`/apartments/${id}`);
    return data;
  },

  updateStatus: async (id: number, status: string): Promise<Apartment> => {
    const { data } = await api.patch<Apartment>(`/apartments/${id}`, { status });
    return data;
  },

  updateNotes: async (id: number, notes: string): Promise<Apartment> => {
    const { data } = await api.patch<Apartment>(`/apartments/${id}`, { notes });
    return data;
  },

  getStats: async (): Promise<DashboardStats> => {
    const { data } = await api.get<DashboardStats>("/apartments/stats");
    return data;
  },

  getRecentPriceChanges: async (hours?: number): Promise<PriceChange[]> => {
    const { data } = await api.get<PriceChange[]>("/apartments/price-changes", {
      params: { hours },
    });
    return data;
  },

  listForMap: async (params: ApartmentSearchParams): Promise<MapApartment[]> => {
    const { data } = await api.get<MapApartment[]>("/apartments/map", { params });
    return data;
  },

  toggleFavorite: async (id: number): Promise<Apartment> => {
    const { data } = await api.post<Apartment>(`/apartments/${id}/favorite`);
    return data;
  },
};

// Filters API
export const filtersApi = {
  list: async (): Promise<Filter[]> => {
    const { data } = await api.get<Filter[]>("/filters");
    return data;
  },

  get: async (id: number): Promise<Filter> => {
    const { data } = await api.get<Filter>(`/filters/${id}`);
    return data;
  },

  create: async (filter: Omit<Filter, "id" | "created_at">): Promise<Filter> => {
    const { data } = await api.post<Filter>("/filters", filter);
    return data;
  },

  update: async (
    id: number,
    filter: Partial<Omit<Filter, "id" | "created_at">>
  ): Promise<Filter> => {
    const { data } = await api.put<Filter>(`/filters/${id}`, filter);
    return data;
  },

  bulkUpdate: async (filterIds: number[], changes: FilterBulkChanges): Promise<Filter[]> => {
    const { data } = await api.patch<Filter[]>("/filters/bulk", {
      filter_ids: filterIds,
      changes,
    });
    return data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/filters/${id}`);
  },

  toggle: async (id: number): Promise<Filter> => {
    const { data } = await api.post<Filter>(`/filters/${id}/toggle`);
    return data;
  },

  duplicate: async (
    id: number,
    name: string,
    discordChannelId: string,
    searchArea?: string | null
  ): Promise<Filter> => {
    const { data } = await api.post<Filter>(`/filters/${id}/duplicate`, {
      name,
      discord_channel_id: discordChannelId,
      ...(searchArea !== undefined && { search_area: searchArea }),
    });
    return data;
  },
};

// Scrapes API
export const scrapesApi = {
  list: async (params: {
    status?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<ScrapeExecution>> => {
    const { data } = await api.get<PaginatedResponse<ScrapeExecution>>("/scrapes", {
      params,
    });
    return data;
  },

  get: async (id: number): Promise<ScrapeExecutionDetail> => {
    const { data } = await api.get<ScrapeExecutionDetail>(`/scrapes/${id}`);
    return data;
  },
};

// Settings API
export const settingsApi = {
  get: async (): Promise<Settings> => {
    const { data } = await api.get<Settings>("/settings");
    return data;
  },

  update: async (settings: Settings): Promise<Settings> => {
    const { data } = await api.put<Settings>("/settings", settings);
    return data;
  },
};

// Geocode API
export const geocodeApi = {
  geocode: async (
    address: string
  ): Promise<{ latitude: number; longitude: number } | { error: string }> => {
    const { data } = await api.get<{ latitude: number; longitude: number } | { error: string }>(
      "/geocode",
      {
        params: { address },
      }
    );
    return data;
  },
};

// Messages API
export const messagesApi = {
  create: async (apartmentId: number, content: string): Promise<{ id: number }> => {
    const { data } = await api.post<{ id: number }>("/messages", {
      apartment_id: apartmentId,
      content,
    });
    return data;
  },
};

// Admin API
export const adminApi = {
  getNotificationStats: async (): Promise<NotificationStats> => {
    const { data } = await api.get<NotificationStats>("/admin/notifications/stats");
    return data;
  },

  getApartmentsWithMatchCounts: async (status?: string): Promise<ApartmentWithMatchCount[]> => {
    const { data } = await api.get<ApartmentWithMatchCount[]>("/admin/notifications/apartments", {
      params: { status },
    });
    return data;
  },

  reevaluate: async (apartmentIds?: number[]): Promise<void> => {
    await api.post("/admin/notifications/reevaluate", {
      apartment_ids: apartmentIds,
    });
  },

  getRejections: async (params?: {
    reason?: string;
    filter_id?: number;
    limit?: number;
  }): Promise<RejectedApartment[]> => {
    const { data } = await api.get<RejectedApartment[]>("/admin/rejections", {
      params,
    });
    return data;
  },

  getRejectionStats: async (): Promise<RejectionStats> => {
    const { data } = await api.get<RejectionStats>("/admin/rejections/stats");
    return data;
  },
};

export default api;
